import { useEffect, useState } from 'react'
import DataTable from 'react-data-table-component';
import { FaEdit } from 'react-icons/fa';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { selectAuth, setActiveTab } from '../../redux/features/authSlice';
import { useGetDeliveryParcelListQuery, useUpdateDeliveryStatusMutation } from '../../redux/api/parcelApi/parcelApi';
import CustomLoading from '../../components/CustomLoading';
import CustomModal from '../../components/CustomModal';
import * as Yup from 'yup';
import moment from 'moment';
import { useFormik } from 'formik';
import { IAuthUser } from '../../redux/features/authSlice/authSlice.types';
import jwt_decode from 'jwt-decode';

const statusColor = {
    Pending: "bg-[#FF8C00]/10 text-[#FF8C00]",
    Assigned: "bg-[#9ACD32]/10 text-[#9ACD32]",
    "Set Out": "bg-[#FFCE0D]/10 text-[#FFCE0D]",
    Delivered: "bg-[#3CB043]/10 text-[#3CB043]",
    Returning: "bg-[#1D92D8]/10 text-[#1D92D8]",
    Returned: "bg-[#1D92D8]/10 text-[#1D92D8]",
    Failed: "bg-[#161616]/10 text-[#161616]",
    Restored: "bg-[#001780]/10 text-[#001780]",
    Cancelled: "bg-[#C00000]/10 text-[#C00000]",
    Picked: "bg-[#176A73]/10 text-[#176A73]",
    Stored: "bg-[#9ACD32]/10 text-[#9ACD32]",
};

const DeliveryParcelList = () => {
    const dispatch = useDispatch();
    const { pathname } = useLocation();
    const [filterText, setFilterText] = useState("");
    const { token } = useSelector(selectAuth);
    const authData: IAuthUser = token ? jwt_decode(token) : null;
    const { data: deliveryParcel, isLoading: deliveryParcelLoading } = useGetDeliveryParcelListQuery(
        authData?.id
    );
    const [deliveryParcelList, setdeliveryParcelList] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [updatePickupStatus, deliveryStatusResult] = useUpdateDeliveryStatusMutation();
    const [deliveryParcelInfo, setDeliveryParcelInfo] = useState(null);

    const validationSchema = Yup.object().shape({
        status: Yup.string().required("Status is required"),
    });

    const initialValues = {
        status: ""
    };

    const formik = useFormik({
        initialValues,
        validationSchema,
        onSubmit: async (values, { resetForm }) => {
            const data = {
                status: values.status
            };
            try {
                await updatePickupStatus({ data, id: deliveryParcelInfo?.id })
                toast.success("Status updated successfully!");
                resetForm();
                setShowModal(false);
            } catch (error) {
                console.log(error);
                toast.error(error?.data?.message || "Something went wrong!");
            }
        },
    });

    const {
        handleSubmit,
        values,
        touched,
        errors,
        setFieldValue,
        handleBlur,
    } = formik;

    const columns = [
        {
            name: 'Date',
            selector: row => moment(row.created_at).format("MMM Do YY"),
            sortable: true,
        },
        {
            name: "Customer Name",
            selector: (row) => row?.customer_name,
            sortable: true,
        },
        {
            name: "Customer Address",
            selector: (row) => row?.customer_address,
            sortable: true,
        },
        {
            name: "Customer Phone",
            selector: (row) => row?.customer_phone,
            sortable: true,
        },
        {
            name: "Pickup Location",
            selector: (row) => row?.pickup_location,
            sortable: true,
        },
        {
            name: "Collect Amount",
            selector: (row) => "৳ " + row?.collect_amount,
            sortable: true,
        },
        {
            name: 'Weight',
            selector: row => row.weight + " kg",
            sortable: true,
        },
        {
            name: "Pickup Status",
            selector: (row) => (
                <div
                    className={`py-1 w-20 text-center rounded ${statusColor[row?.pickup_status]
                        }`}
                >
                    {row?.pickup_status}
                </div>
            ),
            sortable: true,
        },
        {
            name: "Delivery Status",
            selector: (row) => (
                <div
                    className={`py-1 w-20 text-center rounded ${statusColor[row?.delivery_status]
                        }`}
                >
                    {row?.delivery_status}
                </div>
            ),
            sortable: true,
        },
        {
            name: "Action",
            cell: (row) => (
                <div
                    onClick={() => {
                        if (row?.delivery_status !== 'Returned') {
                            setDeliveryParcelInfo(row);
                            setShowModal(true);
                        } else {
                            toast.info("Parcel is already returned!");
                        }

                    }}
                    className={`flex flex-row gap-2 items-center justify-center py-1 w-20 text-center rounded border bg-secondary/10 border-secondary text-secondary cursor-pointer transition-all hover:bg-secondary hover:animate-pulse hover:text-white`}
                >
                    <FaEdit /> Edit
                </div>
            ),
        },
    ];

    const filteredParcelList = deliveryParcelList
        ?.filter(
            (item) =>
                item?.customer_name && item?.customer_name.toLowerCase().includes(filterText.toLowerCase())
        )
        ?.reverse();

    useEffect(() => {
        if (deliveryParcel) {
            setdeliveryParcelList(deliveryParcel?.filter(data => data?.delivery_status !== 'Delivered')?.filter(data => data?.delivery_status !== 'Restored')?.filter(data => data?.delivery_status !== 'Returning'));
            if (deliveryParcelInfo?.delivery_status !== 'Pending') {
                setFieldValue('status', deliveryParcelInfo?.delivery_status);
            }
        }
    }, [deliveryParcel, deliveryParcelInfo]);

    useEffect(() => {
        if (pathname === "/delivery-parcel-list") {
            dispatch(setActiveTab("Delivery Parcel List"));
        }
    }, [dispatch, pathname]);
    return (
        <div className="w-full flex flex-col gap-10">
            <div className="bg-white shadow-lg rounded-lg p-4 justify-between flex flex-row gap-10">
                <div className="flex flex-col justify-between w-full gap-4">
                    <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
                        <div className="text-2xl font-bold text-text_1">Delivery Parcel List</div>
                    </div>
                    <div className="flex flex-row gap-4 items-center w-full">
                        <input
                            type="text"
                            className="flex-grow p-2 w-full focus:ring-0 border-2 rounded-lg focus:border-secondary focus:outline-none"
                            placeholder={`Search by customer name`}
                            onChange={(e) => setFilterText(e.target.value)}
                            value={filterText}
                        />
                        <button
                            disabled={!filterText}
                            onClick={() => setFilterText("")}
                            className={`py-2 w-24 text-center border border-warning text-warning rounded bg-warning/10 flex items-center justify-center ${!filterText
                                ? ""
                                : "cursor-pointer transition-all hover:bg-warning hover:animate-pulse hover:text-white"
                                }`}
                        >
                            Clear
                        </button>
                    </div>
                </div>
                <div className="h-40 hidden lg:flex">
                    <img
                        src="https://res.cloudinary.com/di23omfvx/image/upload/v1692428968/self_driving_car_Road_1_npjom3.gif"
                        alt="self drive"
                        className="h-full w-full"
                    />
                </div>
            </div>
            <div className="overflow-x-auto bg-white shadow-lg rounded-lg p-2">
                <DataTable
                    columns={columns}
                    data={filteredParcelList}
                    pagination
                    persistTableHead
                    highlightOnHover
                />
            </div>

            <CustomModal
                showModal={showModal}
                setShowModal={setShowModal}
                submitButton={true}
                handleSubmit={handleSubmit}
                title={'Assign Rider'}
                className="md:min-w-[60%] lg:min-w-[40vw] xl:min-w-[29vw]"
            >
                <form
                    onSubmit={handleSubmit}
                    className="w-full flex flex-col gap-2 mt-5 min-w-[70%]"
                >
                    <select onBlur={handleBlur}
                        value={values.status}
                        className="w-full border-2 p-2 rounded-lg focus:outline-none focus:border-secondary"
                        onChange={(e) => setFieldValue('status', e.target.value)}>
                        <option value="">Select an option</option>
                        <option value="Set Out">Set Out</option>
                        <option value="Delivered">Delivered</option>
                        <option value="Failed">Failed</option>
                        <option value="Restored">Restored</option>
                    </select>
                    {touched.status && errors.status ? (
                        <span className="text-error text-sm mt-1">
                            {errors.status}
                        </span>
                    ) : null}
                </form>
            </CustomModal>

            {(deliveryParcelLoading || deliveryStatusResult.isLoading) && <CustomLoading />}
        </div>
    )
}

export default DeliveryParcelList